import React from "react";

import { strings } from "@perceptron/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@perceptron/ui/components/card";
import { Icon } from "@perceptron/ui/components/icon";

import { AnnotationItem } from "./AnnotationItem";
import { EditorAnnotation } from "../store/annotationTypes";

interface AnnotationListProps {
  annotations: EditorAnnotation[];
  onSelect: (id: string | null) => void;
  onUpdateLabel: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}

function AnnotationSection({
  title,
  annotations,
  onSelect,
  onUpdateLabel,
  onDelete,
}: {
  title: string;
  annotations: EditorAnnotation[];
  onSelect: (id: string | null) => void;
  onUpdateLabel: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}) {
  if (annotations.length === 0) return null;

  return (
    <div className="min-w-0">
      <h3 className="mb-2 text-sm font-medium text-zinc-400">{title}</h3>
      <ul className="min-w-0 space-y-1">
        {annotations.map((annotation) => (
          <AnnotationItem
            annotation={annotation}
            key={annotation.id}
            onDelete={onDelete}
            onSelect={onSelect}
            onUpdateLabel={onUpdateLabel}
          />
        ))}
      </ul>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4">
        <Icon className="size-16 text-white/60" name="annotation-sidebar-empty" />
        <p className="text-center font-inter text-base text-white/60">{strings.annotationSidebar.noAnnotations}</p>
      </div>
    </div>
  );
}

export function AnnotationList({ annotations, onSelect, onUpdateLabel, onDelete }: AnnotationListProps) {
  // Group annotations by type
  const pointAnnotations = annotations.filter((a) => a.type === "point");
  const boxAnnotations = annotations.filter((a) => a.type === "box");

  const content =
    annotations.length === 0 ? (
      <EmptyState />
    ) : (
      <div className="h-full overflow-y-auto">
        <div className="min-w-0 space-y-4 p-3">
          <AnnotationSection
            annotations={pointAnnotations}
            title={strings.annotationSidebar.points}
            onDelete={onDelete}
            onSelect={onSelect}
            onUpdateLabel={onUpdateLabel}
          />
          <AnnotationSection
            annotations={boxAnnotations}
            title={strings.annotationSidebar.boxes}
            onDelete={onDelete}
            onSelect={onSelect}
            onUpdateLabel={onUpdateLabel}
          />
        </div>
      </div>
    );

  return (
    <Card className="flex h-full w-64 flex-col rounded-none border-0 bg-[#171717]">
      <CardHeader className="pt-3">
        <CardTitle className="font-inter text-xl font-normal text-white">{strings.annotationSidebar.title}</CardTitle>
      </CardHeader>
      <CardContent className="relative flex-1 overflow-hidden pl-2 pr-0">{content}</CardContent>
    </Card>
  );
}
